import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { getIsAcceptanceTest } from '../utils/testUtils';
import { useGlobalLoading } from './loadingHooks';

/**
 * Hook to handle redirection when a user is ineligible for a page/feature.
 *
 * Handles common patterns around:
 * - Waiting for app to be ready
 * - Checking eligibility conditions
 * - Skipping redirects in acceptance tests
 *
 * @example
 * ```
 * useRedirectIfIneligible({
 *   condition: () => !isUngatedForFeature,
 *   redirectPath: '/suite'
 * });
 * ```
 *
 * @param options.condition - Function that returns true if user is ineligible, false if user is eligible,
 *                          or undefined while loading. Defaults to () => false
 * @param options.redirectPath - Path to redirect to when ineligible. Defaults to '/suite'
 */
export const useRedirectIfIneligible = ({
  condition = () => false,
  redirectPath = '/suite'
}) => {
  const isAppReady = useGlobalLoading();
  const isAcceptanceTest = getIsAcceptanceTest();
  const history = useHistory();
  useEffect(() => {
    if (!isAppReady) return;
    const result = condition();
    if (result && !isAcceptanceTest) {
      history.replace(redirectPath);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAppReady, condition, redirectPath, isAcceptanceTest]);
};